import { useEffect, useState, type MouseEvent } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { dispatchDeskNavigation } from '@/msgdeskRuntime/deskNavigation'
import { siteMenuSkin as skin } from './SiteMenuSkin'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectAccessState } from '@/msgdeskRuntime/state'

export function SiteMenu({
  isEditorOpen,
  settingsOpen,
  onToggleSettings,
}: {
  isEditorOpen: boolean
  settingsOpen: boolean
  onToggleSettings: () => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { state } = useSubsStore()
  // LOCKED-SCREEN: a show/time-locked desk shows ONLY the locked template.
  const locked = !selectAccessState(state).showUnlocked

  useEffect(() => {
    if (!isOpen) return
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isEditorOpen) setIsOpen(false)
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [isEditorOpen, isOpen])

  function handleEdit() {
    dispatchEditorAction({ action: 'open-edit-message' })
  }

  function handleSave(event: MouseEvent<HTMLButtonElement>) {
    dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'site-menu:before-save' })
  }

  function handleClose(event: MouseEvent<HTMLButtonElement>) {
    dispatchDeskNavigation({ mode: 'msg', target: event.currentTarget })
  }

  return (
    <nav {...legacyContractDataAttrs('sidebar.root')} className={`${skin.root}${locked ? ' hidden' : ''}`} style={locked ? { display: 'none' } : undefined} aria-label="Msgdesk site controls">
      <button
        type="button"
        className={skin.brand}
        aria-expanded={isOpen}
        aria-controls="md-site-menu-actions"
        onClick={() => setIsOpen(open => !open)}
      >
        msgdesk
      </button>
      <div
        id="md-site-menu-actions"
        className={`${skin.actions} ${isOpen ? skin.actionsOpen : skin.actionsClosed}`}
        aria-hidden={!isOpen}
      >
        {isEditorOpen ? (
          <>
            <button type="button" className={`${skin.action} ${skin.actionPrimary}`} onClick={handleSave}>Save</button>
            <button type="button" className={skin.action} aria-pressed={settingsOpen} onClick={onToggleSettings}>
              {settingsOpen ? 'Content' : 'Configure'}
            </button>
            <button type="button" className={`${skin.action} ${skin.actionDanger}`} onClick={handleClose}>Close</button>
          </>
        ) : (
          <button type="button" className={`${skin.action} ${skin.actionPrimary}`} onClick={handleEdit}>Edit</button>
        )}
        {/* POSSESSION-MODEL: desk state sits where identity used to; the
            identity keeps the bar's extreme right (= page top-right). */}
        <DeskStateBadge skin={{
          root: 'flex items-center whitespace-nowrap px-2 text-[11px] font-medium',
          possessedOpen: 'text-emerald-700',
          possessedPin: 'text-emerald-700',
          locked: 'text-slate-400',
          label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2',
          form: 'ml-1.5 inline-flex items-center gap-1',
          input: 'h-[26px] w-[110px] rounded border border-slate-300 bg-white px-2 text-[12px] text-slate-900 outline-none',
          button: 'h-[26px] cursor-pointer rounded px-2 text-[12px] text-slate-700 hover:bg-slate-100',
        }} />
        <AuthEntry skin={{
          root: 'flex items-center gap-1',
          signInButton: skin.action,
          identity: 'flex items-center gap-1',
          identityLink: `${skin.action} max-w-[110px] overflow-hidden text-ellipsis whitespace-nowrap`,
          signOutButton: skin.action,
        }} />
      </div>
      {/* PARENTS-MENU: shared skeleton leaf, site skin. Sibling of the
          collapsible actions (that container is overflow-hidden for its
          animation); the nav root is overflow-visible, so the dropdown
          anchors below the item. Renders nothing without a parent chain. */}
      <ParentsMenu skin={{
        root: 'relative flex h-full items-stretch',
        trigger: `${skin.action} gap-1 bg-transparent`,
        triggerOpen: 'bg-slate-100 text-slate-950',
        count: 'text-[10px] text-slate-400',
        list: 'absolute left-0 top-full z-20 mt-1 flex min-w-[200px] max-w-[80vw] flex-col gap-0.5 rounded-md border border-black/10 bg-white/95 p-2 text-xs shadow-[0_4px_18px_rgba(15,23,42,0.16)] backdrop-blur-md',
        link: 'truncate rounded px-2 py-1 text-blue-700 no-underline hover:bg-blue-50 hover:underline',
        homeLink: 'mt-0.5 truncate rounded border-t border-slate-200 px-2 py-1 pt-1.5 text-slate-500 no-underline hover:bg-slate-100',
      }} />
    </nav>
  )
}
